import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from "recoil";
// material
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { formatKstDate } from "@utils/kst";
import {
  SIGNUP_REQUEST_LIMIT,
  adminRefreshTick,
  adminSignupHighlightId,
  adminSignupRequestOne,
  adminSignupRequestStatus,
  adminSignupRequestsQuery,
} from "@states/admin";
import { SignupRequestType, SignupStatus } from "types/admin";

const STATUS_LABEL: Record<SignupStatus, { text: string; color: "warning" | "success" | "default" }> = {
  pending: { text: "대기", color: "warning" },
  approved: { text: "승인", color: "success" },
  rejected: { text: "거절", color: "default" },
};

//: 기본이 «대기» 다. 처리된 것이 섞이면 «승인했는데 왜 아직 있지» 가 되고, 쌓이면
//: 목록 상한을 다 차지해서 대기 건이 통째로 사라진다 (`states/admin.ts` 주석).
const STATUS_OPTIONS = [
  { value: "pending", label: "대기" },
  { value: "approved", label: "승인" },
  { value: "rejected", label: "거절" },
  { value: "", label: "전체" },
];

/**
 * 서버가 주는 오류 문구. **문자열일 때만** 쓴다.
 *
 * `HTTPException(detail="...")` 는 문자열이지만 pydantic 검증 실패(422)의 `detail` 은
 * **객체 배열**이다. 구분하지 않고 화면에 넣으면 React 가
 * "Objects are not valid as a React child" 로 터진다.
 */
function errorText(error: any, fallback: string): string {
  return typeof error?.detail === "string" ? error.detail : fallback;
}

export default function SignupRequestPanel() {
  const loadable = useRecoilValueLoadable(adminSignupRequestsQuery);
  const [statusFilter, setStatusFilter] = useRecoilState(adminSignupRequestStatus);
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<SignupRequestType | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  // 삼항식을 그대로 쓰면 매 렌더마다 새 배열이 되고, 아래 `useMemo` · `useEffect` 의
  // 의존성이 늘 바뀐다. 그러면 하이라이트된 행으로 스크롤하는 효과가 **매 렌더마다**
  // 다시 돈다.
  const requests = useMemo<SignupRequestType[]>(
    () => (loadable.state === "hasValue" ? loadable.contents : []),
    [loadable.state, loadable.contents],
  );

  // 관리자 알림 메일이 `/admin/users?request=<신청ID>` 로 링크한다. 그 링크 자체는
  // 아무 권한도 주지 않는다 — 여기까지 왔다는 것은 이미 관리자로 로그인했다는 뜻이다.
  const highlighted = searchParams.get("request");
  const setHighlightId = useSetRecoilState(adminSignupHighlightId);
  const oneLoadable = useRecoilValueLoadable(adminSignupRequestOne);

  // **목록에서 찾지 않는다.** 목록은 이제 기본이 «대기» 고 상한도 있어서, 며칠 지난
  // 메일이 가리키는 (이미 처리된) 신청은 거기 없다. 서버에 그 id 로 직접 묻는다.
  useEffect(() => {
    setHighlightId(highlighted);
  }, [highlighted, setHighlightId]);

  const highlightedRequest =
    oneLoadable.state === "hasValue" && oneLoadable.contents ? oneLoadable.contents : undefined;

  // 그 신청이 지금 목록에도 있으면 따로 그리지 않는다 — 같은 줄이 두 번 보인다.
  const highlightedIsInList = useMemo(
    () => requests.some((request) => request.id === highlighted),
    [requests, highlighted],
  );

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlightedRequest]);

  const refresh = () => bumpRefresh((tick) => tick + 1);

  const clearHighlight = () => {
    searchParams.delete("request");
    setSearchParams(searchParams, { replace: true });
  };

  const approve = async (request: SignupRequestType) => {
    setBusyId(request.id);
    try {
      await axios.post(`signup-requests/${request.id}/approve`);
      // 비밀번호를 없앤 뒤로 승인 메일에는 임시 비밀번호가 아니라 **로그인 링크**가
      // 실린다. 문구가 그대로 남아 있어서 관리자가 «비밀번호를 알려 줬다» 고 알고
      // 있었다.
      openSnackbar(`${request.name}님의 가입을 승인했습니다. 로그인 링크를 메일로 보냈습니다.`, "success");
      refresh();
    } catch (error: any) {
      openSnackbar(errorText(error, "승인하지 못했습니다. 잠시 후 다시 시도해주세요."), "error");
      // 실패 이유가 "이미 처리됨" 일 수 있다. 목록을 다시 읽어 실제 상태를 보여준다.
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    try {
      await axios.post(`signup-requests/${rejecting.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      openSnackbar(`${rejecting.name}님의 신청을 거절했습니다.`, "success");
      setRejecting(null);
      setRejectReason("");
      refresh();
    } catch (error: any) {
      openSnackbar(errorText(error, "거절하지 못했습니다. 잠시 후 다시 시도해주세요."), "error");
      refresh();
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = requests.filter((request) => request.status === "pending").length;

  // 응답에 총계가 없어서 «잘렸다» 를 알 방법이 이것뿐이다. 받은 수가 상한과 같으면
  // 더 있을 수 있다고 봐야 한다. 조용히 잘리는 것보다 낫다.
  const maybeTruncated = requests.length >= SIGNUP_REQUEST_LIMIT;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1">가입 신청</Typography>
        {pendingCount > 0 && <Chip size="small" color="warning" label={`대기 ${pendingCount}`} />}
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          select
          size="small"
          label="상태"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          SelectProps={{ native: true }}
          // «전체» 의 값이 `""` 라 MUI 가 칸이 비었다고 보고 라벨을 안쪽에 둔다 — 그러면
          // 라벨과 «전체» 가 겹친다. 유저 목록의 등급·멤버십 칸과 같은 처방이다.
          InputLabelProps={{ shrink: true }}
          sx={{ width: 140 }}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      </Stack>

      {maybeTruncated && (
        <Alert severity="warning">
          신청이 {SIGNUP_REQUEST_LIMIT}건을 넘어 일부만 보고 있습니다. 상태를 좁혀서 보세요.
        </Alert>
      )}

      {/* 메일이 가리키는 신청이 지금 목록 밖에 있을 때(이미 처리됐거나 상한 밖) 그
          한 건만 따로 보여 준다. 목록에도 있으면 아래 표에서 노랗게 칠해지므로 생략한다. */}
      {highlighted && highlightedRequest && !highlightedIsInList && (
        <Alert severity="info" onClose={clearHighlight}>
          메일이 가리키는 신청 — <b>{highlightedRequest.name}</b> ({highlightedRequest.email}) ·{" "}
          <b>{STATUS_LABEL[highlightedRequest.status].text}</b>
          {highlightedRequest.decidedAt && ` · ${formatKstDate(highlightedRequest.decidedAt)} 처리됨`}
          {highlightedRequest.rejectReason && ` · ${highlightedRequest.rejectReason}`}
        </Alert>
      )}

      {highlighted && !highlightedRequest && oneLoadable.state === "hasValue" && (
        <Alert severity="warning" onClose={clearHighlight}>
          메일이 가리키는 신청(<code>{highlighted}</code>)을 찾지 못했습니다. 이미 지워졌을 수 있습니다.
        </Alert>
      )}

      {loadable.state === "hasError" && (
        // 조회가 실패하면 빈 표만 그려져 «처리할 신청이 없다» 로 읽혔다(f2-3). 실패라고 말한다.
        <Alert severity="error">
          가입 신청 목록을 불러오지 못했습니다. 새로고침하거나 잠시 후 다시 시도해주세요.
        </Alert>
      )}

      {loadable.state === "hasValue" && requests.length === 0 ? (
        <Alert severity="info">
          {statusFilter === "pending"
            ? "처리를 기다리는 가입 신청이 없습니다."
            : "해당하는 가입 신청이 없습니다."}
        </Alert>
      ) : (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>이름</TableCell>
                <TableCell>이메일</TableCell>
                <TableCell>신청일</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="right">처리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => {
                const isHighlighted = request.id === highlighted;
                const { text, color } = STATUS_LABEL[request.status];
                return (
                  <TableRow
                    key={request.id}
                    ref={isHighlighted ? highlightRef : undefined}
                    sx={isHighlighted ? { bgcolor: "warning.lighter" } : undefined}
                  >
                    <TableCell>{request.name}</TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>{formatKstDate(request.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={text} color={color} />
                      {request.rejectReason && (
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                          {request.rejectReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {request.status === "pending" ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <LoadingButton
                            size="small"
                            variant="contained"
                            loading={busyId === request.id}
                            onClick={() => approve(request)}
                          >
                            승인
                          </LoadingButton>
                          <Button
                            size="small"
                            color="inherit"
                            disabled={busyId === request.id}
                            onClick={() => setRejecting(request)}
                          >
                            거절
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {formatKstDate(request.decidedAt)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={Boolean(rejecting)} onClose={() => setRejecting(null)} fullWidth maxWidth="xs">
        <DialogTitle>가입 신청 거절</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {rejecting?.name}({rejecting?.email})님의 신청을 거절합니다.
            {/* 신청자에게는 메일이 가지 않는다. 관리자가 오해하지 않도록 화면에 적어 둔다. */}
            <br />
            <b>신청자에게는 메일이 가지 않습니다.</b> 사유는 이 화면에만 남습니다.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="사유 (선택)"
            inputProps={{ maxLength: 255 }}
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setRejecting(null)}>
            취소
          </Button>
          <LoadingButton color="error" variant="contained" loading={Boolean(busyId)} onClick={reject}>
            거절
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
